import { sharedLib } from "@shared/services/shared.service";
import {
  BasicBotData,
  BotData,
  BotStatus,
  BotType,
  DisplayCurrency,
} from "@shared/types";
import { useMemo } from "react";
import { BOT_TYPES } from "@shared/common/constants";
import { DISPLAY_CURRENCIES } from "common/constants";
import { BotSummaryData } from "features/dashboard/common/types";
import { toFixed } from "@shared/utils/numbers";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { dashboardSelectors } from "@shared/slices/dashboard.slice";
import { useSelector } from "react-redux";
import {
  useSelectUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";

const useBotsSummary = () => {
  const { botsData: dcaBotsData, isLoading: dcaBotsLoading } = useBotsData({
    botType: BotType.ASAP,
  });
  const { botsData: signalsBotsData, isLoading: signalsBotsLoading } =
    useBotsData({ botType: BotType.SIGNAL });
  const { botsData: gridBotsData, isLoading: gridBotsLoading } = useBotsData({
    botType: BotType.GRID,
  });
  const { botsData: tradingViewBotsData, isLoading: tradingViewLoading } =
    useBotsData({
      botType: BotType.TRADING_VIEW,
    });
  const { isReadWrite } = useSubUserPermissions();
  const user = useSelectUser();

  let botTypeToData: { [key in BotType]?: BasicBotData[] | undefined } = {
    [BotType.SIGNAL]: signalsBotsData,
  };
  if (!isReadWrite && !user?.isWhopExperience()) {
    botTypeToData = {
      ...botTypeToData,
      [BotType.ASAP]: dcaBotsData,
      [BotType.GRID]: gridBotsData,
      [BotType.TRADING_VIEW]: tradingViewBotsData,
    };
  }

  const isLoading =
    dcaBotsLoading ||
    signalsBotsLoading ||
    gridBotsLoading ||
    tradingViewLoading;

  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const getNumActiveBots = (botsData?: BasicBotData[]) =>
    botsData?.filter((bot: BasicBotData) => bot.status === BotStatus.ACTIVE)
      .length || 0;

  const getSum = (
    botsData: BasicBotData[] | undefined,
    key: keyof Pick<
      BotData,
      | "number_of_open_trades"
      | "number_of_closed_trades"
      | "take_profits_filled"
    >
  ) => {
    return (
      botsData?.reduce(
        (sum: number, bot: BasicBotData) => sum + (bot[key] || 0),
        0
      ) || 0
    );
  };

  const getChange = (
    botsData: BasicBotData[] | undefined,
    displayCurrency: DisplayCurrency,
    isOpen?: boolean
  ) => {
    return toFixed(
      botsData?.reduce(
        (sum: number, bot: BasicBotData) =>
          sum +
          (bot[isOpen ? "open_trades_change" : "change"]?.[
            displayCurrency === DISPLAY_CURRENCIES.USD ? 0 : 1
          ] || 0),
        0
      ) || 0,
      precision
    );
  };

  const getRealisedPnl = (
    botsData: BasicBotData[] | undefined,
    displayCurrency: DisplayCurrency
  ) => {
    return toFixed(
      botsData?.reduce(
        (sum: number, bot: BasicBotData) =>
          sum + (bot.realised_pnl?.[displayCurrency] || 0),
        0
      ) || 0,
      precision
    );
  };

  const botsSummaryData = useMemo(() => {
    let summaryData: BotSummaryData[] = [];
    Object.entries(botTypeToData).forEach(([botTypeStr, botsData]) => {
      const botType = Number(botTypeStr) as BotType;
      let botSummaryData: BotSummaryData = {
        botType,
        numBots: botsData?.length || 0,
        numActiveBots: getNumActiveBots(botsData),
      };
      if (botType === BotType.GRID) {
        botSummaryData = {
          ...botSummaryData,
          takeProfitsFilled: getSum(botsData, "take_profits_filled"),
          realisedPnl: {
            USD: getRealisedPnl(botsData, DISPLAY_CURRENCIES.USD),
            BTC: getRealisedPnl(botsData, DISPLAY_CURRENCIES.BTC),
          },
        };
      } else {
        botSummaryData = {
          ...botSummaryData,
          numOpenTrades: getSum(botsData, "number_of_open_trades"),
          numClosedTrades: getSum(botsData, "number_of_closed_trades"),
          openTradesChange: {
            USD: getChange(botsData, DISPLAY_CURRENCIES.USD, true),
            BTC: getChange(botsData, DISPLAY_CURRENCIES.BTC, true),
          },
          closedTradesChange: {
            USD: getChange(botsData, DISPLAY_CURRENCIES.USD),
            BTC: getChange(botsData, DISPLAY_CURRENCIES.BTC),
          },
        };
      }
      summaryData.push(botSummaryData);
    });
    return summaryData;
  }, [
    dcaBotsData,
    signalsBotsData,
    gridBotsData,
    tradingViewBotsData,
    precision,
  ]);

  const sortedBots = useMemo(() => {
    return botsSummaryData
      .slice()
      .sort(
        (a, b) => BOT_TYPES.indexOf(a.botType) - BOT_TYPES.indexOf(b.botType)
      )
      .sort((a, b) => b.numBots - a.numBots)
      .sort((a, b) => b.numActiveBots - a.numActiveBots);
  }, [botsSummaryData]);

  return {
    sortedBots,
    isLoading,
  };
};

export default useBotsSummary;

interface UseBotsDataProps {
  botType: BotType;
}

const useBotsData = ({ botType }: UseBotsDataProps) => {
  const args = useMemo(
    () => ({ bot_type: botType, include_archived: false }),
    [botType]
  );
  const queryData = sharedLib.sharedApi.bots.useGetBotsDataQuery(args);
  const botsSelector = useMemo(
    () => dashboardSelectors.selectBotsData(botType),
    [botType]
  );
  const botsData = useSelector(botsSelector);

  return { botsData, isLoading: queryData.isLoading };
};
